import axios from 'src/utils/axios';

import { HOST_API, AUTH0_API, PATH_AFTER_LOGIN, LOCAL_STORAGE_KEYS } from 'src/config-global';

// ----------------------------------------------------------------------
// Login runs through Strapi's Auth0 connect flow (see docs/AUTHENTICATION.md):
//   startAuth0Login() → Strapi /api/connect/auth0 → Auth0 → Strapi
//   → /auth/auth0/callback (Auth0CallbackPage) → exchange for the Strapi JWT.
// The Strapi JWT is the only login state the app keeps.
// ----------------------------------------------------------------------

/** sessionStorage key for the page to open after the login round trip */
const RETURN_TO_KEY = 'auth_return_to';

/** localStorage key for the Strapi user returned at login (username, email) */
const AUTH_USER_KEY = 'auth_user';

export type SessionUser = { id: number; username: string; email: string };

function jwtDecode(token: string) {
  const base64Url = token.split('.')[1];
  const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
  const jsonPayload = decodeURIComponent(
    window
      .atob(base64)
      .split('')
      .map((c) => `%${`00${c.charCodeAt(0).toString(16)}`.slice(-2)}`)
      .join('')
  );
  return JSON.parse(jsonPayload);
}

// ----------------------------------------------------------------------

export const isValidToken = (accessToken: string | null) => {
  if (!accessToken) {
    return false;
  }

  try {
    const decoded = jwtDecode(accessToken);
    return decoded.exp > Date.now() / 1000;
  } catch {
    return false;
  }
};

// ----------------------------------------------------------------------

let expiredTimer: ReturnType<typeof setTimeout> | undefined;

/** Clears the stored token when it expires, so the next load starts a new login */
export const tokenExpired = (exp: number) => {
  const timeLeft = exp * 1000 - Date.now();

  clearTimeout(expiredTimer);
  expiredTimer = setTimeout(() => {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.auth);
  }, timeLeft);
};

// ----------------------------------------------------------------------

/** The stored Strapi JWT, or null. Kept in localStorage so tabs and reloads share the login. */
export const getSession = (): string | null => localStorage.getItem(LOCAL_STORAGE_KEYS.auth);

export const getSessionUser = (): SessionUser | null => {
  try {
    const stored = localStorage.getItem(AUTH_USER_KEY);
    return stored ? (JSON.parse(stored) as SessionUser) : null;
  } catch {
    return null;
  }
};

export const setSession = (accessToken: string | null, user?: SessionUser) => {
  if (accessToken) {
    localStorage.setItem(LOCAL_STORAGE_KEYS.auth, accessToken);
    if (user) {
      localStorage.setItem(
        AUTH_USER_KEY,
        JSON.stringify({ id: user.id, username: user.username, email: user.email })
      );
    }

    axios.defaults.headers.common.Authorization = `Bearer ${accessToken}`;

    const { exp } = jwtDecode(accessToken);
    tokenExpired(exp);
  } else {
    localStorage.removeItem(LOCAL_STORAGE_KEYS.auth);
    localStorage.removeItem(AUTH_USER_KEY);

    delete axios.defaults.headers.common.Authorization;
  }
};

// ----------------------------------------------------------------------

/** Only same-app paths are allowed as a return target (no open redirects) */
const toSafeReturnTo = (returnTo?: string | null) =>
  returnTo && returnTo.startsWith('/') && !returnTo.startsWith('//') ? returnTo : PATH_AFTER_LOGIN;

/** Starts the login: a full-page navigation to Strapi, which redirects to Auth0 */
export const startAuth0Login = (returnTo?: string) => {
  sessionStorage.setItem(RETURN_TO_KEY, toSafeReturnTo(returnTo));
  window.location.assign(`${HOST_API}/api/connect/auth0`);
};

/** Returns (and forgets) the page to open after login */
export const consumeReturnTo = () => {
  const returnTo = sessionStorage.getItem(RETURN_TO_KEY);
  sessionStorage.removeItem(RETURN_TO_KEY);
  return toSafeReturnTo(returnTo);
};

/**
 * Auth0 logout URL, which also ends the Auth0 session (otherwise the next login would
 * skip the login form). returnTo must be in the Auth0 application's Allowed Logout URLs.
 * Returns null when the Auth0 domain or client ID isn't configured.
 */
export const buildAuth0LogoutUrl = (returnTo: string) => {
  if (!AUTH0_API.domain || !AUTH0_API.clientId) {
    return null;
  }

  const params = new URLSearchParams({ client_id: AUTH0_API.clientId, returnTo });
  return `https://${AUTH0_API.domain}/v2/logout?${params.toString()}`;
};
